import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { organizationSettingsService } from "@/services/organizationSettings.service/organizationSettings.api";
import { AlarmClock, AlertTriangle, Bell, BellOff, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

function SlaAlertIntervalSettings() {
  const [minutes, setMinutes] = useState("10");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [data, err] = await organizationSettingsService.getSettings();
      if (cancelled) return;
      if (err || !data) {
        toast.error(err?.msg || "Não foi possível carregar as configurações");
        setLoading(false);
        return;
      }
      setMinutes(String(data.slaAlertIntervalMinutes ?? 10));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSave() {
    const value = Number(minutes);
    if (!Number.isFinite(value) || value < 1) {
      toast.error("Informe um número de minutos maior ou igual a 1");
      return;
    }
    setSaving(true);
    const [, err] = await organizationSettingsService.updateSlaAlertInterval(value);
    setSaving(false);
    if (err) {
      toast.error(err.msg || "Erro ao salvar");
      return;
    }
    toast.success("Intervalo de alerta de SLA atualizado!");
  }

  return (
    <div>
      <h3 className="flex items-center gap-2 text-base font-semibold">
        <AlarmClock className="h-4 w-4" />
        Alerta de SLA de resposta
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Quando um ticket entrar em risco de estourar o SLA de resposta (ou já tiver estourado), os
        agentes são avisados por e-mail e push, repetindo a cada intervalo abaixo até alguém
        responder.
      </p>

      <div className="mt-4 rounded-lg border border-border p-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-1.5">
              <Label htmlFor="sla-alert-interval" className="text-sm">
                Repetir alerta a cada (minutos)
              </Label>
              <Input
                id="sla-alert-interval"
                type="number"
                min={1}
                className="w-32"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
              />
            </div>
            <Button size="sm" className="gap-2" disabled={saving} onClick={handleSave}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function NotificationSettings() {
  const { state, loading, subscribe, unsubscribe } = usePushNotifications();

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold">Notificações push</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Receba um aviso no navegador/dispositivo quando um chamado for criado ou tiver uma nova
            mensagem — só para chamados em que você está envolvido (nunca para uma ação sua).
          </p>
        </div>

        <div className="rounded-lg border border-border p-4">
          {state === "unsupported" && (
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>Seu navegador não suporta notificações push.</p>
            </div>
          )}

          {state === "denied" && (
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                As notificações foram bloqueadas para este site. Habilite manualmente nas
                configurações do navegador (ícone de cadeado na barra de endereço) e recarregue a
                página.
              </p>
            </div>
          )}

          {state === "subscribed" && (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <p>Notificações push ativadas neste navegador.</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                disabled={loading}
                onClick={() => void unsubscribe()}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="h-4 w-4" />}
                Desativar
              </Button>
            </div>
          )}

          {state === "unsubscribed" && (
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                Notificações push desativadas neste navegador.
              </p>
              <Button size="sm" className="gap-2" disabled={loading} onClick={() => void subscribe()}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                Ativar
              </Button>
            </div>
          )}
        </div>
      </div>

      <SlaAlertIntervalSettings />
    </div>
  );
}
